/**
 * Cached fetch of a sector's constituents (persisted query 20006cf3…), shared by every surface that shows them: the
 * Sector Details flyout's stock/ETF tables (`stores/Security.ts`), the dashboard's Sector Stock Leaders card, and the
 * SSS universe filter, which expands sector/top5 roster filters into the flat ticker set it matches against.
 *
 * Each surface used to issue its own request on every mount or drawer open, so the dashboard's strongest sector was
 * fetched again the moment its flyout opened, and reopening a sector waited a full round trip for tables it had just
 * shown (tmi rank 10). One cache entry per (sector, date) now serves them all.
 */
import { goFetchOrThrow } from "@/utils/network-utils";
import type { ApiFetchResponse, PersistedQueryPayload } from "@tmi/types/api/fetch";
import { NotFound } from "@tmi/types/api/responses";
import type { SectorRelatedSecuritiesApiResponse, SectorWithRelatedSecurities } from "@tmi/types/api/related";

export interface SectorConstituents {
    stocks: string[],
    etfs: string[]
}

/** The rows are the sector's constituents as of one market date; a fresh copy is fetched after this long. */
export const SECTOR_CONSTITUENTS_STALE_MS = 30 * 60 * 1000;

type CacheEntry = {
    promise: Promise< SectorRelatedSecuritiesApiResponse | null >,
    // Set once the promise resolves with a sector; an entry without it is still in flight.
    value?: SectorRelatedSecuritiesApiResponse,
    settledAt?: number
};

/**
 * Keyed `${sectorSymbol}|${date}`. Holds in-flight AND settled requests so concurrent callers for the same key
 * single-flight onto one request. A request that resolves to null or rejects is evicted immediately so the next
 * caller retries instead of pinning a permanent miss.
 */
const constituentsCache = new Map< string, CacheEntry >();

function cacheKey( sectorSymbol: string, date: string ): string {
    return `${ sectorSymbol }|${ date }`;
}

function isFresh( entry: CacheEntry ): boolean {
    return entry.settledAt === undefined || Date.now() - entry.settledAt < SECTOR_CONSTITUENTS_STALE_MS;
}

/** Clears the module-scope cache: a test seam, and called by the Security store's reset() when the user changes. */
export function resetConstituentsCache(): void {
    constituentsCache.clear();
}

/**
 * The sector's related securities for the date, or null when the server has none (a response without the sector, or a
 * 404). Every other failure rejects, as goFetchOrThrow does: the timeout reason, a network error or a typed error
 * (Unauthorized, Forbidden, InternalError, …), so the flyout shows its error state instead of empty tables. Plain
 * goFetch resolved a timeout as null, which read as "no constituents". Failures and misses are never cached.
 */
export function fetchSectorRelatedSecurities( sectorSymbol: string, date: string, authToken: string | null | undefined ): Promise< SectorRelatedSecuritiesApiResponse | null > {
    const key = cacheKey( sectorSymbol, date );
    const cached = constituentsCache.get( key );
    if ( cached && isFresh( cached ) ) {
        return cached.promise;
    }

    const evict = () => {
        if ( constituentsCache.get( key ) === entry ) {
            constituentsCache.delete( key );
        }
    };

    const entry: CacheEntry = {
        promise: goFetchOrThrow< SectorWithRelatedSecurities, PersistedQueryPayload >( {
            serverBaseUri: `${ import.meta.env.VITE_SENTIENT_BASE_URI }`,
            endpoint: `${ import.meta.env.VITE_SENTIENT_REL_PATH }`,
            payload: {
                id: "20006cf38b496b6f35cf9a0cdcac8d53",
                variables: {
                    date: date,
                    symbol: sectorSymbol
                }
            },
            authToken: authToken
        } ).then( ( response: ApiFetchResponse< SectorWithRelatedSecurities > ) => {
            const sector = response?.data?.relatedSecuritiesForSector ?? null;
            if ( sector ) {
                entry.value = sector;
                entry.settledAt = Date.now();
            } else {
                evict();
            }
            return sector;
        }, ( err: unknown ) => {
            evict();
            if ( err instanceof NotFound ) {
                return null;
            }

            throw err;
        } )
    };

    constituentsCache.set( key, entry );
    return entry.promise;
}

/**
 * The cached sector for the date when it has already landed and is still fresh, read synchronously so a reopened
 * flyout renders its tables in the same tick instead of flashing a placeholder for a microtask.
 */
export function peekSectorRelatedSecurities( sectorSymbol: string, date: string ): SectorRelatedSecuritiesApiResponse | undefined {
    const cached = constituentsCache.get( cacheKey( sectorSymbol, date ) );
    return cached?.value && isFresh( cached ) ? cached.value : undefined;
}

/**
 * Cached per (sectorSymbol, date); null on any failure — callers degrade to
 * a group entry.
 */
export async function fetchSectorConstituents( sectorSymbol: string, date: string, authToken: string ): Promise< SectorConstituents | null > {
    try {
        const sector = await fetchSectorRelatedSecurities( sectorSymbol, date, authToken );
        if ( !sector ) {
            return null;
        }

        return {
            stocks: sector.stocks.map( ( stock ) => stock.symbol ),
            etfs: sector.etfs.map( ( etf ) => etf.symbol )
        };
    } catch {
        // fetchSectorRelatedSecurities rejects on any failure (a timeout,
        // a dropped connection, an HTTP error) — degrade to null rather than surfacing the exception,
        // callers fall back to a group roster entry.
        return null;
    }
}
